'use client';

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { createNotification } from '@/lib/notifications';
import { useAdminCertifications } from '@/hooks/useAdminCertifications';
import { format } from 'date-fns';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  Clock, 
  FileText,
  Building,
  Building2,
  Star,
  Eye,
  Download,
  AlertTriangle,
  Calendar,
  User,
  Globe,
  MapPin,
  Upload,
  X
} from 'lucide-react';

interface VerificationRequest {
  id: string;
  user_id: string;
  business_name: string;
  verification_level: string;
  status: string;
  requested_at: string;
  documents: any[];
  current_verification_level: string;
  average_rating: number | null;
  total_jobs_completed: number;
  // User Profile fields
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  job_title: string | null;
  department: string | null;
  // Company Profile fields
  business_description: string | null;
  founded_year: number | null;
  employee_count_range: string | null;
  annual_revenue_range: string | null;
  website_url: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  // Review fields for completed requests
  reviewed_at: string | null;
  reviewed_by_admin_id: string | null;
  review_notes: string | null;
  // Admin evidence fields
  business_registration_evidence_url: string | null;
  operational_legitimacy_evidence_url: string | null;
  operational_legitimacy_notes: string | null;
}

export default function VerificationPage() {
  const [requests, setRequests] = useState<VerificationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<VerificationRequest | null>(null);
  const [viewingRequest, setViewingRequest] = useState<VerificationRequest | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);
  const { toast } = useToast();

  // Admin evidence fields for approval
  const [businessRegEvidence, setBusinessRegEvidence] = useState<File | null>(null);
  const [operationalEvidence, setOperationalEvidence] = useState<File | null>(null);
  const [operationalNotes, setOperationalNotes] = useState('');
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);

  // Certifications state
  const {
    certifications,
    loading: certificationsLoading,
    statusFilter,
    setStatusFilter,
    approveCertification,
    rejectCertification,
    getDocumentUrl,
  } = useAdminCertifications();

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState<string | null>(null);
  const [rejectionNotes, setRejectionNotes] = useState('');

  useEffect(() => {
    fetchVerificationRequests();
    // Removed duplicate realtime subscription - useAdminData hook handles this
  }, []);

  const fetchVerificationRequests = async () => {
    try {
      setLoading(true);
      
      // Fetch verification requests and vendor profiles separately 
      const { data: verificationRequests, error: requestsError } = await supabase
        .from('verification_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (requestsError) {
        console.error('Error fetching verification requests:', requestsError);
        throw requestsError;
      }

      // Get unique vendor profile IDs
      const profileIds = [...new Set(verificationRequests?.map(req => req.vendor_profile_id))];
      
      // Fetch vendor profiles enhanced with document URLs and organization name
      const { data: vendorProfiles, error: profilesError } = await supabase
        .from('vendor_profiles_enhanced')
        .select(`
          id, business_name, verification_level, user_id, average_rating,
          total_jobs_completed, organization_id,
          first_name, last_name, phone_number, job_title, department,
          business_description, founded_year, employee_count_range, annual_revenue_range,
          website_url, street_address, city, state, zip_code, country,
          w9_document_url, w9_uploaded_at, business_registration_url, 
          business_registration_uploaded_at, cgl_insurance_url, 
          cgl_insurance_uploaded_at, cgl_insurance_expiry_date,
          organizations!inner(name)
        `)
        .in('id', profileIds);

      if (profilesError) {
        console.error('Error fetching vendor profiles:', profilesError);
        throw profilesError;
      }

      // Create a map for quick lookup
      const profileMap = new Map(vendorProfiles?.map(profile => [profile.id, profile]) || []);

      // Transform the data to match our interface
      const transformedRequests: VerificationRequest[] = (verificationRequests || []).map(req => {
        const profile = profileMap.get(req.vendor_profile_id);
        
        // Build documents array from the new columns
        const documents: any[] = [];
        if (profile?.w9_document_url) {
          documents.push({
            name: 'W-9 Form',
            type: 'W-9',
            url: profile.w9_document_url,
            uploadedAt: profile.w9_uploaded_at,
          });
        }
        if (profile?.business_registration_url) {
          documents.push({
            name: 'Business Registration',
            type: 'Registration',
            url: profile.business_registration_url,
            uploadedAt: profile.business_registration_uploaded_at,
          });
        }
        if (profile?.cgl_insurance_url) {
          documents.push({
            name: 'CGL Insurance Certificate',
            type: 'Insurance',
            url: profile.cgl_insurance_url,
            uploadedAt: profile.cgl_insurance_uploaded_at,
            expiryDate: profile.cgl_insurance_expiry_date,
          });
        }

        return {
          id: req.id,
          user_id: req.requested_by_user_id,
          // Use organization name as source of truth per VENDOR_NAME_GUIDELINES.md
          business_name: (profile?.organizations as any)?.name || profile?.business_name || 'Unknown Business',
          
          verification_level: req.requested_level,
          status: req.status,
          requested_at: req.created_at,
          documents: documents,
          current_verification_level: profile?.verification_level || 'none',
          // Use explicit null check to properly handle 0 ratings
          average_rating: profile?.average_rating != null ? Number(profile.average_rating) : null,
          total_jobs_completed: profile?.total_jobs_completed || 0,
          // User Profile fields
          first_name: profile?.first_name || null,
          last_name: profile?.last_name || null,
          phone_number: profile?.phone_number || null,
          job_title: profile?.job_title || null,
          department: profile?.department || null,
          // Company Profile fields
          business_description: profile?.business_description || null,
          founded_year: profile?.founded_year || null,
          employee_count_range: profile?.employee_count_range || null,
          annual_revenue_range: profile?.annual_revenue_range || null,
          website_url: profile?.website_url || null,
          street_address: profile?.street_address || null,
          city: profile?.city || null,
          state: profile?.state || null,
          zip_code: profile?.zip_code || null,
          country: profile?.country || null,
          // Review fields
          reviewed_at: req.reviewed_at || null,
          reviewed_by_admin_id: req.reviewed_by_admin_id || null,
          review_notes: req.review_notes || null,
          // Admin evidence fields
          business_registration_evidence_url: req.business_registration_evidence_url || null,
          operational_legitimacy_evidence_url: req.operational_legitimacy_evidence_url || null,
          operational_legitimacy_notes: req.operational_legitimacy_notes || null,
        };
      });
      
      setRequests(transformedRequests);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch verification requests",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to upload evidence files
  const uploadEvidenceFile = async (
    file: File, 
    requestId: string, 
    type: 'business-registration' | 'operational-legitimacy'
  ): Promise<string> => {
    const timestamp = Date.now();
    const ext = file.name.split('.').pop()?.toLowerCase() || 'pdf';
    const storagePath = `admin-verification-evidence/${requestId}/${type}/${timestamp}.${ext}`;
    
    const { error } = await supabase.storage
      .from('documents')
      .upload(storagePath, file, {
        contentType: file.type,
        upsert: false,
      });
      
    if (error) throw new Error(`Failed to upload ${type} evidence: ${error.message}`);
    return storagePath;
  };

  const handleReviewSubmit = async () => {
    if (!selectedRequest || !reviewAction) return;

    const approved = reviewAction === 'approve';

    // Validation for approval - require all evidence fields
    if (approved) {
      if (!businessRegEvidence) {
        toast({ 
          title: "Error", 
          description: "Business Registration Evidence is required for approval", 
          variant: "destructive" 
        });
        return;
      }
      if (!operationalEvidence) {
        toast({ 
          title: "Error", 
          description: "Operational Legitimacy Evidence is required for approval", 
          variant: "destructive" 
        });
        return;
      }
      if (!operationalNotes.trim()) {
        toast({ 
          title: "Error", 
          description: "Operational Legitimacy Notes are required for approval", 
          variant: "destructive" 
        });
        return;
      }
    }

    try {
      setIsUploadingEvidence(true);
      
      let businessRegUrl: string | null = null;
      let operationalUrl: string | null = null;
      
      // Upload evidence files if approving
      if (approved && businessRegEvidence && operationalEvidence) {
        businessRegUrl = await uploadEvidenceFile(businessRegEvidence, selectedRequest.id, 'business-registration');
        operationalUrl = await uploadEvidenceFile(operationalEvidence, selectedRequest.id, 'operational-legitimacy');
      }
      
      // Update the verification request status with evidence fields
      const { error: requestError } = await supabase
        .from('verification_requests')
        .update({
          status: approved ? 'approved' : 'rejected',
          review_notes: reviewNotes,
          reviewed_at: new Date().toISOString(),
          reviewed_by_admin_id: (await supabase.auth.getUser()).data.user?.id,
          business_registration_evidence_url: businessRegUrl,
          operational_legitimacy_evidence_url: operationalUrl,
          operational_legitimacy_notes: approved ? operationalNotes : null,
        })
        .eq('id', selectedRequest.id);

      if (requestError) throw requestError;

      // If approved, update the vendor's verification level
      if (approved) {
        const { error: profileError } = await supabase
          .from('vendor_profiles_enhanced')
          .update({
            verification_level: selectedRequest.verification_level,
            is_verified: true,
            verified_at: new Date().toISOString(),
            verified_by_admin_id: (await supabase.auth.getUser()).data.user?.id
          })
          .eq('user_id', selectedRequest.user_id);

        if (profileError) throw profileError;
      }

      // Send notification to vendor
      try {
        const { data: vendorProfile } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('user_id', selectedRequest.user_id)
          .single();

        if (vendorProfile) {
          const notificationTitle = approved ? 'Verification Request Approved' : 'Verification Request Declined';
          const notificationMessage = approved
            ? 'Your vendor verification request has been approved. Your profile now has a verified badge.'
            : `Your verification request has been declined. ${reviewNotes ? `Reason: ${reviewNotes}` : ''}`;

          await createNotification({
            userId: selectedRequest.user_id,
            organizationId: vendorProfile.organization_id,
            title: notificationTitle,
            message: notificationMessage,
            type: approved ? 'success' : 'warning',
            category: 'general',
            relatedEntityType: 'verification_request',
            relatedEntityId: selectedRequest.id,
            actionUrl: '/profile/vendor',
            sendEmail: true,
            emailTemplateName: 'verification_status_update',
            emailTemplateData: {
              approved: approved,
              verification_level: selectedRequest.verification_level,
              review_notes: reviewNotes || '',
              action_url: `${window.location.origin}/profile/vendor`,
            },
            preferenceType: 'verification_updates'
          });
        }
      } catch (notificationError) {
        console.error('Error sending verification notification:', notificationError);
        // Don't fail the verification if notification fails
      }

      // Update the local state
      setRequests(prev => 
        prev.map(req => 
          req.id === selectedRequest.id 
            ? { 
                ...req, 
                status: approved ? 'approved' : 'rejected',
                current_verification_level: approved ? selectedRequest.verification_level : req.current_verification_level,
                business_registration_evidence_url: businessRegUrl,
                operational_legitimacy_evidence_url: operationalUrl,
                operational_legitimacy_notes: approved ? operationalNotes : null,
              }
            : req
        )
      );

      toast({
        title: "Success",
        description: `Verification request ${approved ? 'approved' : 'rejected'}`,
      });

      // Reset form
      setSelectedRequest(null);
      setReviewNotes('');
      setReviewAction(null);
      setBusinessRegEvidence(null);
      setOperationalEvidence(null);
      setOperationalNotes('');
    } catch (error) {
      console.error('Error processing verification request:', error);
      toast({
        title: "Error",
        description: "Failed to process verification request",
        variant: "destructive",
      });
    } finally {
      setIsUploadingEvidence(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge variant="default" className="bg-success"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getVerificationLevelBadge = (level: string) => {
    const colors = {
      none: 'bg-gray-500',
      basic: 'bg-blue-500',
      enhanced: 'bg-green-500',
      premium: 'bg-purple-500'
    };
    
    return (
      <Badge className={`${colors[level as keyof typeof colors] || 'bg-gray-500'} text-white`}>
        <Shield className="h-3 w-3 mr-1" />
        {level.toUpperCase()}
      </Badge>
    );
  };

  // Certification handlers
  const handleApprove = async (certId: string) => {
    await approveCertification(certId);
  };

  const handleRejectClick = (certId: string) => {
    setSelectedCert(certId);
    setRejectionNotes('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (selectedCert && rejectionNotes.trim()) {
      await rejectCertification(selectedCert, rejectionNotes);
      setRejectDialogOpen(false);
      setSelectedCert(null);
      setRejectionNotes('');
    }
  };

  const handleViewDocument = async (documentUrl: string) => {
    const signedUrl = await getDocumentUrl(documentUrl);
    if (signedUrl) {
      window.open(signedUrl, '_blank');
    }
  };

  const pendingRequests = requests.filter(req => req.status === 'pending');
  const completedRequests = requests.filter(req => req.status !== 'pending');

  const pendingCertsCount = certifications.filter(c => c.verification_status === 'pending').length;
  const verifiedCertsCount = certifications.filter(c => c.verification_status === 'verified').length;
  const rejectedCertsCount = certifications.filter(c => c.verification_status === 'rejected').length;

  if (loading || certificationsLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Vendor Review Queue</h1>
          <p className="text-muted-foreground mt-1">
            Review and approve vendor verification requests and certifications
          </p>
        </div>

        {/* Parent-Level Tabs */}
        <Tabs defaultValue="verifications" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="verifications">Vendor Verification</TabsTrigger>
            <TabsTrigger value="certifications">Certification Verification</TabsTrigger>
          </TabsList>

          {/* Vendor Verification Tab */}
          <TabsContent value="verifications" className="space-y-6 mt-6">

            {/* Verification Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{pendingRequests.length}</p>
                  <p className="text-sm text-muted-foreground">Pending Reviews</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{completedRequests.filter(r => r.status === 'approved').length}</p>
                  <p className="text-sm text-muted-foreground">Approved</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{completedRequests.filter(r => r.status === 'rejected').length}</p>
                  <p className="text-sm text-muted-foreground">Rejected</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Shield className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{requests.length}</p>
                  <p className="text-sm text-muted-foreground">Total Requests</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

            {/* Verification Requests Nested Tabs */}
            <Tabs defaultValue="pending" className="space-y-6">
          <TabsList>
            <TabsTrigger value="pending">Pending ({pendingRequests.length})</TabsTrigger>
            <TabsTrigger value="completed">Completed ({completedRequests.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>Pending Verification Requests</CardTitle>
                <CardDescription>
                  Review vendor verification requests requiring approval
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingRequests.length > 0 ? (
                  <div className="space-y-4">
                    {pendingRequests.map((request) => (
                      <div key={request.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <Avatar className="h-12 w-12">
                              <AvatarFallback>
                                <Building className="h-6 w-6" />
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <h3 className="font-semibold">{request.business_name}</h3>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-muted-foreground">Current:</span>
                                {getVerificationLevelBadge(request.current_verification_level)}
                                <span className="text-xs text-muted-foreground">→ Requesting:</span>
                                {getVerificationLevelBadge(request.verification_level)}
                              </div>
                            </div>
                          </div>
                          <div className="text-right space-y-2">
                            <div className="flex items-center gap-2">
                              {getStatusBadge(request.status)}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4" />
                                {request.average_rating != null ? request.average_rating.toFixed(1) : 'No reviews'}
                              </div>
                              <div>{request.total_jobs_completed} jobs</div>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Requested {new Date(request.requested_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{request.documents.length} documents uploaded</span>
                          </div>
                          <div className="flex gap-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setSelectedRequest(request)}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  Review
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                  <DialogTitle>Review Verification Request</DialogTitle>
                                  <DialogDescription>
                                    {selectedRequest?.business_name} - {selectedRequest?.verification_level} verification
                                  </DialogDescription>
                                </DialogHeader>
                                
                                {selectedRequest && (
                                  <div className="space-y-6 max-h-[60vh] overflow-y-auto">
                                    {/* Contact Information */}
                                    <div className="space-y-4">
                                      <h4 className="font-medium flex items-center gap-2">
                                        <User className="h-4 w-4" />
                                        Contact Information
                                      </h4>
                                      <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                          <Label className="text-muted-foreground">Contact Name</Label>
                                          <p className="font-medium">{[selectedRequest.first_name, selectedRequest.last_name].filter(Boolean).join(' ') || 'N/A'}</p>
                                        </div>
                                        <div>
                                          <Label className="text-muted-foreground">Job Title</Label>
                                          <p className="font-medium">{selectedRequest.job_title || 'N/A'}</p>
                                        </div>
                                        <div>
                                          <Label className="text-muted-foreground">Phone Number</Label>
                                          <p className="font-medium">{selectedRequest.phone_number || 'N/A'}</p>
                                        </div>
                                        <div>
                                          <Label className="text-muted-foreground">Department</Label>
                                          <p className="font-medium">{selectedRequest.department || 'N/A'}</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Company Profile Overview */}
                                    <div className="space-y-4">
                                      <h4 className="font-medium flex items-center gap-2">
                                        <Building2 className="h-4 w-4" />
                                        Company Profile Overview
                                      </h4>
                                      <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                          <Label className="text-muted-foreground">Business Name</Label>
                                          <p className="font-medium">{selectedRequest.business_name}</p>
                                        </div>
                                        <div>
                                          <Label className="text-muted-foreground">Founded</Label>
                                          <p className="font-medium">{selectedRequest.founded_year || 'N/A'}</p>
                                        </div>
                                        <div>
                                          <Label className="text-muted-foreground">Employees</Label>
                                          <p className="font-medium">{selectedRequest.employee_count_range || 'N/A'}</p>
                                        </div>
                                        <div>
                                          <Label className="text-muted-foreground">Annual Revenue</Label>
                                          <p className="font-medium">{selectedRequest.annual_revenue_range || 'N/A'}</p>
                                        </div>
                                        <div>
                                          <Label className="text-muted-foreground">Website</Label>
                                          {selectedRequest.website_url ? (
                                            <a 
                                              href={selectedRequest.website_url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="font-medium text-primary hover:underline flex items-center gap-1"
                                            >
                                              <Globe className="h-3 w-3" />
                                              {selectedRequest.website_url.replace(/^https?:\/\//, '')}
                                            </a>
                                          ) : (
                                            <p className="font-medium">N/A</p>
                                          )}
                                        </div>
                                        <div className="col-span-2">
                                          <Label className="text-muted-foreground">Address</Label>
                                          <p className="font-medium flex items-center gap-1">
                                            {[
                                              selectedRequest.street_address,
                                              selectedRequest.city,
                                              selectedRequest.state,
                                              selectedRequest.zip_code,
                                              selectedRequest.country
                                            ].filter(Boolean).join(', ') || 'N/A'}
                                          </p>
                                        </div>
                                        {selectedRequest.business_description && (
                                          <div className="col-span-2">
                                            <Label className="text-muted-foreground">Business Description</Label>
                                            <p className="font-medium text-muted-foreground">{selectedRequest.business_description}</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Verification Request */}
                                    <div className="space-y-4">
                                      <h4 className="font-medium flex items-center gap-2">
                                        <Shield className="h-4 w-4" />
                                        Verification Request
                                      </h4>
                                      <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                          <Label className="text-muted-foreground">Current Level</Label>
                                          <div className="mt-1">{getVerificationLevelBadge(selectedRequest.current_verification_level)}</div>
                                        </div>
                                        <div>
                                          <Label className="text-muted-foreground">Requested Level</Label>
                                          <div className="mt-1">{getVerificationLevelBadge(selectedRequest.verification_level)}</div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Documents */}
                                    <div className="space-y-4">
                                      <h4 className="font-medium">Uploaded Documents</h4>
                                      {selectedRequest.documents.length > 0 ? (
                                        <div className="space-y-2">
                                          {selectedRequest.documents.map((doc, index) => (
                                            <div key={index} className="flex items-center justify-between p-3 border rounded">
                                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <FileText className="h-4 w-4 flex-shrink-0" />
                                                <div className="min-w-0">
                                                  <span className="text-sm font-medium">{doc.name}</span>
                                                  <div className="flex items-center gap-2 flex-wrap">
                                                    <Badge variant="outline" className="text-xs">{doc.type}</Badge>
                                                    {doc.uploadedAt && (
                                                      <span className="text-xs text-muted-foreground">
                                                        Uploaded {format(new Date(doc.uploadedAt), 'MMM d, yyyy')}
                                                      </span>
                                                    )}
                                                    {doc.expiryDate && (
                                                      <div className="flex items-center gap-1">
                                                        <Calendar className="h-3 w-3 text-muted-foreground" />
                                                        <span className="text-xs text-muted-foreground">
                                                          Expires {format(new Date(doc.expiryDate), 'MMM d, yyyy')}
                                                        </span>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                              <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={async () => {
                                                  // Get signed URL for the document
                                                  const { data, error } = await supabase.storage
                                                    .from('documents')
                                                    .createSignedUrl(doc.url, 3600);
                                                  if (data?.signedUrl) {
                                                    window.open(data.signedUrl, '_blank');
                                                  }
                                                }}
                                              >
                                                <Download className="h-4 w-4 mr-2" />
                                                Download
                                              </Button>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="p-4 border rounded bg-muted/50 text-center">
                                          <AlertTriangle className="h-5 w-5 mx-auto text-amber-500 mb-2" />
                                          <p className="text-sm text-muted-foreground">
                                            No documents uploaded for this request
                                          </p>
                                        </div>
                                      )}
                                    </div>

                                    {/* Review Form */}
                                    <div className="space-y-4">
                                      <h4 className="font-medium">Review Decision</h4>
                                      <div className="space-y-3">
                                        <div>
                                          <Label>Action</Label>
                                          <Select value={reviewAction || ''} onValueChange={(value: 'approve' | 'reject') => setReviewAction(value)}>
                                            <SelectTrigger>
                                              <SelectValue placeholder="Select action" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="approve">Approve</SelectItem>
                                              <SelectItem value="reject">Reject</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                              <div>
                                <Label>Review Notes</Label>
                                <p className="text-sm text-muted-foreground mb-2">
                                  These notes will be visible to the vendor.
                                </p>
                                <Textarea
                                            value={reviewNotes}
                                            onChange={(e) => setReviewNotes(e.target.value)}
                                            placeholder="Add notes about your decision..."
                                            rows={3}
                                          />
                                        </div>

                                        {/* Admin Evidence Fields - Required for approval */}
                                        <div className="space-y-4 pt-4 border-t">
                                          <p className="text-sm text-muted-foreground">
                                            The following evidence is required when approving a verification request.
                                          </p>
                                          
                                          {/* Business Registration Evidence Upload */}
                                            <div className="space-y-2">
                                              <Label>Business Registration Evidence *</Label>
                                              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                                                <input
                                                  type="file"
                                                  accept=".pdf,.jpg,.jpeg,.png"
                                                  onChange={(e) => setBusinessRegEvidence(e.target.files?.[0] || null)}
                                                  className="hidden"
                                                  id="business-reg-evidence"
                                                />
                                                <label htmlFor="business-reg-evidence" className="cursor-pointer">
                                                  {businessRegEvidence ? (
                                                    <div className="flex items-center justify-center gap-2">
                                                      <FileText className="h-4 w-4" />
                                                      <span className="text-sm">{businessRegEvidence.name}</span>
                                                      <Button 
                                                        type="button"
                                                        variant="ghost" 
                                                        size="sm" 
                                                        onClick={(e) => { 
                                                          e.preventDefault(); 
                                                          setBusinessRegEvidence(null); 
                                                        }}
                                                      >
                                                        <X className="h-4 w-4" />
                                                      </Button>
                                                    </div>
                                                  ) : (
                                                    <div className="text-muted-foreground">
                                                      <Upload className="h-6 w-6 mx-auto mb-2" />
                                                      <p className="text-sm">Click to upload business registration evidence</p>
                                                    </div>
                                                  )}
                                                </label>
                                              </div>
                                            </div>

                                            {/* Operational Legitimacy Evidence Upload */}
                                            <div className="space-y-2">
                                              <Label>Operational Legitimacy Evidence *</Label>
                                              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                                                <input
                                                  type="file"
                                                  accept=".pdf,.jpg,.jpeg,.png"
                                                  onChange={(e) => setOperationalEvidence(e.target.files?.[0] || null)}
                                                  className="hidden"
                                                  id="operational-evidence"
                                                />
                                                <label htmlFor="operational-evidence" className="cursor-pointer">
                                                  {operationalEvidence ? (
                                                    <div className="flex items-center justify-center gap-2">
                                                      <FileText className="h-4 w-4" />
                                                      <span className="text-sm">{operationalEvidence.name}</span>
                                                      <Button 
                                                        type="button"
                                                        variant="ghost" 
                                                        size="sm" 
                                                        onClick={(e) => { 
                                                          e.preventDefault(); 
                                                          setOperationalEvidence(null); 
                                                        }}
                                                      >
                                                        <X className="h-4 w-4" />
                                                      </Button>
                                                    </div>
                                                  ) : (
                                                    <div className="text-muted-foreground">
                                                      <Upload className="h-6 w-6 mx-auto mb-2" />
                                                      <p className="text-sm">Click to upload operational legitimacy evidence</p>
                                                    </div>
                                                  )}
                                                </label>
                                              </div>
                                            </div>

                                            {/* Operational Legitimacy Notes */}
                                            <div className="space-y-2">
                                              <Label>Operational Legitimacy Notes *</Label>
                                              <Textarea
                                                value={operationalNotes}
                                                onChange={(e) => setOperationalNotes(e.target.value)}
                                                placeholder="Describe the verification steps taken to confirm operational legitimacy..."
                                                rows={3}
                                              />
                                            </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Submit */}
                                    <div className="flex gap-2 justify-end">
                                      <Button variant="outline" onClick={() => setSelectedRequest(null)}>
                                        Cancel
                                      </Button>
                                      <Button 
                                        onClick={handleReviewSubmit}
                                        disabled={
                                          !reviewAction || 
                                          isUploadingEvidence ||
                                          (reviewAction === 'approve' && (!businessRegEvidence || !operationalEvidence || !operationalNotes.trim()))
                                        }
                                        variant={reviewAction === 'approve' ? 'default' : 'destructive'}
                                      >
                                        {isUploadingEvidence ? 'Uploading...' : (reviewAction === 'approve' ? 'Approve Request' : 'Reject Request')}
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No pending requests</h3>
                    <p className="text-muted-foreground">
                      All verification requests have been processed
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="completed">
            <Card>
              <CardHeader>
                <CardTitle>Completed Verification Requests</CardTitle>
                <CardDescription>
                  Previously reviewed verification requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                {completedRequests.length > 0 ? (
                  <div className="space-y-4">
                    {completedRequests.map((request) => (
                      <div key={request.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback>
                                <Building className="h-5 w-5" />
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <h3 className="font-medium">{request.business_name}</h3>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right space-y-1">
                              {getStatusBadge(request.status)}
                              <p className="text-xs text-muted-foreground">
                                {new Date(request.requested_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setViewingRequest(request)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No completed requests</h3>
                    <p className="text-muted-foreground">
                      Completed verification requests will appear here
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
          </TabsContent>

          {/* Certification Verification Tab */}
          <TabsContent value="certifications" className="space-y-6 mt-6">
            {/* Certification Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Clock className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{pendingCertsCount}</p>
                      <p className="text-sm text-muted-foreground">Pending Review</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{verifiedCertsCount}</p>
                      <p className="text-sm text-muted-foreground">Verified</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <XCircle className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{rejectedCertsCount}</p>
                      <p className="text-sm text-muted-foreground">Rejected</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Certifications List */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Certification Submissions</CardTitle>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {certifications.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      No certifications found for the selected filter
                    </div>
                  ) : (
                    certifications.map((cert) => (
                      <Card key={cert.id} className="border-2">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-semibold">
                                  {cert.certification_type_name || cert.certification_name || 'Certification'}
                                </h3>
                                <Badge
                                  variant={
                                    cert.verification_status === 'verified'
                                      ? 'default'
                                      : cert.verification_status === 'rejected'
                                      ? 'destructive'
                                      : 'secondary'
                                  }
                                >
                                  {cert.verification_status}
                                </Badge>
                              </div>
                              {cert.category_name && (
                                <p className="text-sm text-muted-foreground mb-1">
                                  Category: {cert.category_name}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">Vendor:</span>
                                <span>{cert.vendor_name}</span>
                              </div>
                              <div className="flex items-start gap-2 text-sm">
                                <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                                <div>
                                  <span className="font-medium">Issuing Organization:</span>
                                  <p>{cert.issuing_organization}</p>
                                </div>
                              </div>
                              {cert.certificate_number && (
                                <div className="text-sm">
                                  <span className="font-medium">Certificate #:</span> {cert.certificate_number}
                                </div>
                              )}
                            </div>

                            <div className="space-y-2">
                              {cert.issue_date && (
                                <div className="flex items-center gap-2 text-sm">
                                  <Calendar className="h-4 w-4 text-muted-foreground" />
                                  <span className="font-medium">Issued:</span>
                                  <span>{format(new Date(cert.issue_date), 'MMM d, yyyy')}</span>
                                </div>
                              )}
                              {cert.expiry_date && (
                                <div className="flex items-center gap-2 text-sm">
                                  <Calendar className="h-4 w-4 text-muted-foreground" />
                                  <span className="font-medium">Expires:</span>
                                  <span>{format(new Date(cert.expiry_date), 'MMM d, yyyy')}</span>
                                </div>
                              )}
                              <div className="text-sm">
                                <span className="font-medium">Submitted:</span>{' '}
                                {format(new Date(cert.created_at), 'MMM d, yyyy')}
                              </div>
                            </div>
                          </div>

                          {/* Document Section */}
                          {cert.document_url ? (
                            <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-5 w-5 text-primary" />
                                  <div>
                                    <p className="text-sm font-medium">Supporting Document</p>
                                    <p className="text-xs text-muted-foreground">
                                      {cert.document_url.split('/').pop()}
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewDocument(cert.document_url!)}
                                >
                                  <Download className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="mb-4 p-3 bg-muted/20 rounded-lg text-center">
                              <p className="text-sm text-muted-foreground">No document provided</p>
                            </div>
                          )}

                          {/* Review Notes */}
                          {cert.review_notes && (
                            <div className="mb-4 p-3 bg-muted/30 rounded-lg">
                              <p className="text-sm font-medium mb-1">Review Notes:</p>
                              <p className="text-sm text-muted-foreground">{cert.review_notes}</p>
                              {cert.reviewed_at && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Reviewed: {format(new Date(cert.reviewed_at), 'MMM d, yyyy h:mm a')}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          {cert.verification_status === 'pending' && (
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="default"
                                className="flex-1"
                                onClick={() => handleApprove(cert.id)}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="flex-1"
                                onClick={() => handleRejectClick(cert.id)}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* View Completed Request Modal */}
        <Dialog open={!!viewingRequest} onOpenChange={(open) => !open && setViewingRequest(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Verification Request Details</DialogTitle>
              <DialogDescription>
                {viewingRequest?.business_name} - {viewingRequest?.verification_level} verification
              </DialogDescription>
            </DialogHeader>
            
            {viewingRequest && (
              <div className="space-y-6 max-h-[60vh] overflow-y-auto">
                {/* Decision Summary Banner */}
                <div className={`p-4 rounded-lg flex items-center gap-3 ${
                  viewingRequest.status === 'approved' 
                    ? "bg-green-500/10 border border-green-500/20" 
                    : "bg-red-500/10 border border-red-500/20"
                }`}>
                  {viewingRequest.status === 'approved' ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <div>
                    <p className="font-medium capitalize">{viewingRequest.status}</p>
                    {viewingRequest.reviewed_at && (
                      <p className="text-sm text-muted-foreground">
                        Reviewed on {format(new Date(viewingRequest.reviewed_at), 'MMM d, yyyy \'at\' h:mm a')}
                      </p>
                    )}
                  </div>
                </div>

                {/* Review Notes (if any) */}
                {viewingRequest.review_notes && (
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <Label className="text-muted-foreground">Review Notes</Label>
                    <p className="mt-1">{viewingRequest.review_notes}</p>
                  </div>
                )}

                {/* Admin Evidence Section - Only for approved requests */}
                {viewingRequest.status === 'approved' && (
                  <div className="space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Verification Evidence
                    </h4>
                    
                    {/* Business Registration Evidence */}
                    {viewingRequest.business_registration_evidence_url && (
                      <div className="flex items-center justify-between p-3 border rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          <span className="text-sm font-medium">Business Registration Evidence</span>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={async () => {
                            const { data } = await supabase.storage
                              .from('documents')
                              .createSignedUrl(viewingRequest.business_registration_evidence_url!, 3600);
                            if (data?.signedUrl) {
                              window.open(data.signedUrl, '_blank');
                            }
                          }}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    )}

                    {/* Operational Legitimacy Evidence */}
                    {viewingRequest.operational_legitimacy_evidence_url && (
                      <div className="flex items-center justify-between p-3 border rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          <span className="text-sm font-medium">Operational Legitimacy Evidence</span>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={async () => {
                            const { data } = await supabase.storage
                              .from('documents')
                              .createSignedUrl(viewingRequest.operational_legitimacy_evidence_url!, 3600);
                            if (data?.signedUrl) {
                              window.open(data.signedUrl, '_blank');
                            }
                          }}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    )}

                    {/* Operational Legitimacy Notes */}
                    {viewingRequest.operational_legitimacy_notes && (
                      <div className="p-4 border rounded-lg bg-muted/30">
                        <Label className="text-muted-foreground">Operational Legitimacy Notes</Label>
                        <p className="mt-1">{viewingRequest.operational_legitimacy_notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Contact Information */}
                <div className="space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-muted-foreground">Contact Name</Label>
                      <p className="font-medium">{[viewingRequest.first_name, viewingRequest.last_name].filter(Boolean).join(' ') || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Job Title</Label>
                      <p className="font-medium">{viewingRequest.job_title || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Phone Number</Label>
                      <p className="font-medium">{viewingRequest.phone_number || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Department</Label>
                      <p className="font-medium">{viewingRequest.department || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Company Profile Overview */}
                <div className="space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Company Profile Overview
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-muted-foreground">Business Name</Label>
                      <p className="font-medium">{viewingRequest.business_name}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Founded</Label>
                      <p className="font-medium">{viewingRequest.founded_year || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Employees</Label>
                      <p className="font-medium">{viewingRequest.employee_count_range || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Annual Revenue</Label>
                      <p className="font-medium">{viewingRequest.annual_revenue_range || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Website</Label>
                      {viewingRequest.website_url ? (
                        <a 
                          href={viewingRequest.website_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-medium text-primary hover:underline flex items-center gap-1"
                        >
                          <Globe className="h-3 w-3" />
                          {viewingRequest.website_url.replace(/^https?:\/\//, '')}
                        </a>
                      ) : (
                        <p className="font-medium">N/A</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Label className="text-muted-foreground">Address</Label>
                      <p className="font-medium">
                        {[
                          viewingRequest.street_address,
                          viewingRequest.city,
                          viewingRequest.state,
                          viewingRequest.zip_code,
                          viewingRequest.country
                        ].filter(Boolean).join(', ') || 'N/A'}
                      </p>
                    </div>
                    {viewingRequest.business_description && (
                      <div className="col-span-2">
                        <Label className="text-muted-foreground">Business Description</Label>
                        <p className="font-medium text-muted-foreground">{viewingRequest.business_description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Verification Request */}
                <div className="space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Verification Request
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-muted-foreground">Current Level</Label>
                      <div className="mt-1">{getVerificationLevelBadge(viewingRequest.current_verification_level)}</div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Requested Level</Label>
                      <div className="mt-1">{getVerificationLevelBadge(viewingRequest.verification_level)}</div>
                    </div>
                  </div>
                </div>

                {/* Documents */}
                <div className="space-y-4">
                  <h4 className="font-medium">Uploaded Documents</h4>
                  {viewingRequest.documents.length > 0 ? (
                    <div className="space-y-2">
                      {viewingRequest.documents.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText className="h-4 w-4 flex-shrink-0" />
                            <div className="min-w-0">
                              <span className="text-sm font-medium">{doc.name}</span>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="text-xs">{doc.type}</Badge>
                                {doc.uploadedAt && (
                                  <span className="text-xs text-muted-foreground">
                                    Uploaded {format(new Date(doc.uploadedAt), 'MMM d, yyyy')}
                                  </span>
                                )}
                                {doc.expiryDate && (
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">
                                      Expires {format(new Date(doc.expiryDate), 'MMM d, yyyy')}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={async () => {
                              const { data } = await supabase.storage
                                .from('documents')
                                .createSignedUrl(doc.url, 3600);
                              if (data?.signedUrl) {
                                window.open(data.signedUrl, '_blank');
                              }
                            }}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 border rounded bg-muted/50 text-center">
                      <AlertTriangle className="h-5 w-5 mx-auto text-amber-500 mb-2" />
                      <p className="text-sm text-muted-foreground">
                        No documents uploaded for this request
                      </p>
                    </div>
                  )}
                </div>

                {/* Close Button Only */}
                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setViewingRequest(null)}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Rejection Dialog for Certifications */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Certification</DialogTitle>
              <DialogDescription>
                Please provide a reason for rejecting this certification. The vendor will be notified.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="rejection-notes">Rejection Reason *</Label>
              <Textarea
                id="rejection-notes"
                value={rejectionNotes}
                onChange={(e) => setRejectionNotes(e.target.value)}
                placeholder="Explain why this certification is being rejected..."
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRejectConfirm}
                disabled={!rejectionNotes.trim()}
              >
                Reject Certification
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}